import type { PlanDefinition, PlanDefinitionAction } from '@medplum/fhirtypes';

import { ELUVE_PHASE_SYSTEM, isPhaseSystem, slugify } from './constants';
import {
  type ActivityCompiler,
  type CompiledPlan,
  type DecodeContext,
  buildPlanDefinitionBase,
  createActivityCompiler,
  decodeActivityActions,
  decodeSharedFields,
  fromTiming,
  toFhirGoal,
  toTiming,
} from './shared';
import type { PhasedPlanSpec, PlanPhaseSpec } from './spec';

function phaseToAction(
  phase: PlanPhaseSpec,
  index: number,
  activities: ActivityCompiler,
): PlanDefinitionAction {
  return {
    id: `phase-${index + 1}`,
    title: phase.title,
    description: phase.description,
    code: [
      {
        coding: [
          {
            system: ELUVE_PHASE_SYSTEM,
            code: slugify(phase.title),
            display: phase.title,
          },
        ],
      },
    ],
    timingTiming: toTiming(phase),
    condition: phase.exitCriteria
      ? [
          {
            kind: 'applicability',
            expression: {
              language: 'text/fhirpath',
              // Expression's exp-1 invariant requires `expression` (Medplum
              // enforces it), so a prose-only spec mirrors the criteria there.
              expression: phase.exitExpression ?? phase.exitCriteria,
              description: phase.exitCriteria,
            },
          },
        ]
      : undefined,
    // Prescribed exercises ride as tagged child actions referencing their
    // ActivityDefinitions.
    action: activities.compile(phase.activities, `phase-${index + 1}`),
  };
}

function actionToPhase(
  action: PlanDefinitionAction,
  ctx?: DecodeContext,
): PlanPhaseSpec {
  const expression = action.condition?.find(
    (c) => c.kind === 'applicability',
  )?.expression;
  const exitCriteria = expression?.description ?? expression?.expression;
  // Foreign plans may carry the phase length as timingDuration; authored plans
  // as timingTiming.repeat.boundsDuration. Accept both.
  const fromTimingTiming = fromTiming(action.timingTiming);
  const durationDays =
    fromTimingTiming.durationDays ??
    (action.timingDuration?.code === 'd'
      ? action.timingDuration.value
      : undefined);
  return {
    title: action.title ?? 'Phase',
    description: action.description,
    exitCriteria,
    exitExpression:
      expression?.expression && expression.expression !== exitCriteria
        ? expression.expression
        : undefined,
    durationDays,
    cadence: fromTimingTiming.cadence,
    activities: decodeActivityActions(action.action, ctx),
  };
}

// True when the action carries a phase coding — the signal an unstamped plan
// is phased. Accepts the eluve system and the legacy seeded systems
// (orchard/myodetox) so seeded PlanDefinitions are recognized as phased too.
export function isPhaseAction(action: PlanDefinitionAction): boolean {
  return (action.code ?? []).some((cc) =>
    (cc.coding ?? []).some((c) => isPhaseSystem(c.system)),
  );
}

export function phasedSpecToResources(spec: PhasedPlanSpec): CompiledPlan {
  const activities = createActivityCompiler(spec.title);
  return {
    planDefinition: {
      ...buildPlanDefinitionBase(
        spec,
        'phased',
        (spec.goals ?? []).map((g) => toFhirGoal(g)),
      ),
      action: spec.phases.map((phase, i) =>
        phaseToAction(phase, i, activities),
      ),
    },
    activityDefinitions: activities.definitions(),
  };
}

// Tolerant decode for rendering stored plans: every layer beyond the phase
// spine is optional, so foreign plans (no shape extension, no section codes)
// decode too. Returns null when there is no phase spine at all.
export function phasedFhirToSpec(
  pd: PlanDefinition,
  ctx?: DecodeContext,
): PhasedPlanSpec | null {
  const actions = pd.action ?? [];
  if (actions.length === 0) {
    return null;
  }
  return {
    shape: 'phased',
    ...decodeSharedFields(pd),
    phases: actions.map((action) => actionToPhase(action, ctx)),
  };
}
